import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import {
    FileText,
    CheckCircle,
    Clock,
    DollarSign,
    Shield,
    Ship,
    Calendar,
    ArrowRight,
    Plus,
    Eye,
    TrendingUp,
    BarChart3,
    AlertCircle,
    Loader2
} from 'lucide-react';
import api from '../../services/api';
import Badge from '../../components/ui/Badge';

// ========== HELPER: COVERAGE PROGRESS RING ==========
function CoverageRing({ active, total }) {
    const percentage = total > 0 ? Math.round((active / total) * 100) : 0;
    const circumference = 2 * Math.PI * 40;
    const offset = circumference - (percentage / 100) * circumference;

    return (
        <div className="relative w-20 h-20">
            <svg className="w-20 h-20 -rotate-90" viewBox="0 0 100 100">
                <circle
                    cx="50" cy="50" r="40"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    className="text-gray-200 dark:text-slate-700"
                />
                <circle
                    cx="50" cy="50" r="40"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    strokeLinecap="round"
                    className="text-[#3a663f] dark:text-green-400 transition-all duration-1000 ease-out"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-extrabold text-gray-800 dark:text-white">{percentage}%</span>
            </div>
        </div>
    );
}

export default function ClientDashboard() {
    const { user } = useSelector((state) => state.auth);
    const [statusFilter, setStatusFilter] = useState('all');

    // Fetch policies with proper data extraction
    const { data: policiesData, isLoading, error, refetch } = useQuery(
        'client-policies',
        () => api.get('/policies').then((res) => res.data),
        {
            staleTime: 30000,
            refetchInterval: 60000,
        }
    );

    // Extract policies from response
    const policies = policiesData?.data || policiesData || [];
    const policiesArray = Array.isArray(policies) ? policies : [];

    // Compute stats
    const totalPolicies = policiesArray.length || 0;
    const activePolicies = policiesArray.filter((p) => p.payment_status === 'paid' || p.payment_status === 'active').length || 0;
    const pendingPolicies = policiesArray.filter((p) => p.payment_status === 'unpaid' || p.payment_status === 'pending').length || 0;
    const totalValue = policiesArray.reduce((sum, p) => sum + Number(p.sum_insured || 0), 0) || 0;

    // Recent policies (last 5)
    const recentPolicies = policiesArray.slice(0, 5) || [];

    // Filter policies based on selected status
    const filteredPolicies = policiesArray.filter((p) => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'paid') return p.payment_status === 'paid' || p.payment_status === 'active';
        if (statusFilter === 'unpaid') return p.payment_status === 'unpaid' || p.payment_status === 'pending';
        return true;
    });

    if (isLoading) return <SkeletonLoader />;

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
                <div className="text-center max-w-md p-8">
                    <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                        <AlertCircle className="w-10 h-10 text-red-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Failed to Load Policies</h3>
                    <p className="text-gray-500 dark:text-slate-400">Unable to fetch your policies. Please try again.</p>
                    <button
                        onClick={() => refetch()}
                        className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#3a663f] text-white rounded-lg font-medium hover:bg-[#2d5231] transition-colors"
                    >
                        <Loader2 className="w-4 h-4" />
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* ======= HERO BANNER ======= */}
                <div className="relative bg-gradient-to-br from-[#1e3b29] via-[#3a663f] to-[#4d8c54] rounded-2xl p-8 md:p-10 text-white overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/3 translate-x-1/4" />
                    <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/10 rounded-full translate-y-1/3 -translate-x-1/4" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />

                    <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Shield className="w-5 h-5 text-green-300" />
                                <span className="text-green-200 text-xs uppercase tracking-widest font-semibold">Client Dashboard</span>
                            </div>
                            <h1 className="text-3xl md:text-4xl font-extrabold">
                                Welcome back, {user?.full_name || 'Valued Client'} 👋
                            </h1>
                            <p className="text-green-100/80 max-w-md">
                                Manage your marine insurance policies, track coverage, and generate new certificates.
                            </p>
                            <div className="flex flex-wrap gap-2 mt-3">
                                <span className="px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs font-medium text-green-100 border border-white/10 flex items-center gap-1.5">
                                    <CheckCircle className="w-3 h-3" />
                                    {activePolicies} Active
                                </span>
                                <span className="px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs font-medium text-green-100 border border-white/10 flex items-center gap-1.5">
                                    <DollarSign className="w-3 h-3" />
                                    ${totalValue.toLocaleString()} Insured
                                </span>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/cargo"
                                className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-white/30 text-white font-semibold py-3 px-6 rounded-xl hover:bg-white hover:text-[#3a663f] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                            >
                                <Plus className="w-5 h-5" />
                                New Certificate
                            </Link>
                            <Link
                                to="/policies"
                                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-white font-medium py-3 px-6 rounded-xl hover:bg-white/20 transition-all duration-300"
                            >
                                <Eye className="w-5 h-5" />
                                View All Policies
                            </Link>
                        </div>
                    </div>
                </div>

                {/* ======= STAT CARDS + COVERAGE RING ======= */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard
                        icon={FileText}
                        label="Total Policies"
                        value={totalPolicies}
                        gradient="from-[#2d5232] to-[#3a663f]"
                    />
                    <StatCard
                        icon={CheckCircle}
                        label="Active Cover"
                        value={activePolicies}
                        gradient="from-[#3a663f] to-[#4d8c54]"
                    />
                    <StatCard
                        icon={DollarSign}
                        label="Total Insured"
                        value={`$${totalValue.toLocaleString()}`}
                        gradient="from-[#4d8c54] to-[#5fa06a]"
                    />
                    {/* Coverage Ring Card */}
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 p-5 flex flex-col items-center justify-center space-y-3 hover:shadow-xl transition-shadow">
                        <CoverageRing active={activePolicies} total={totalPolicies} />
                        <p className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">Coverage Rate</p>
                        <p className="text-sm text-gray-500 dark:text-slate-400 text-center">
                            {activePolicies} of {totalPolicies} active
                        </p>
                    </div>
                </div>

                {/* ======= RECENT ACTIVITY + POLICY SECTION ======= */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Recent Activity Feed */}
                    <div className="lg:col-span-1 bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                <span className="w-1.5 h-6 bg-[#3a663f] rounded-full" />
                                Recent Activity
                            </h3>
                            {recentPolicies.length > 0 && (
                                <Link
                                    to="/policies"
                                    className="text-xs text-[#3a663f] dark:text-green-400 hover:text-[#2d5231] font-medium"
                                >
                                    View All
                                </Link>
                            )}
                        </div>
                        {recentPolicies.length > 0 ? (
                            <div className="space-y-4">
                                {recentPolicies.map((policy) => (
                                    <div key={policy.id} className="flex items-start gap-3 pb-4 border-b border-gray-100 dark:border-slate-700 last:border-0 last:pb-0">
                                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${policy.payment_status === 'paid' || policy.payment_status === 'active'
                                                ? 'bg-green-500'
                                                : 'bg-amber-500'
                                            }`} />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
                                                {policy.policy_number}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                                {policy.vessel_name || 'N/A'}
                                            </p>
                                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                                                {new Date(policy.issued_date).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-slate-700 mb-3">
                                    <Clock className="w-6 h-6 text-gray-400 dark:text-slate-500" />
                                </div>
                                <p className="text-sm text-gray-400 dark:text-slate-500">No recent activity</p>
                            </div>
                        )}
                    </div>

                    {/* Policy Grid */}
                    <div className="lg:col-span-2">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                <FileText className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                Your Policies
                                <span className="text-sm font-normal text-gray-400 dark:text-slate-500">
                                    ({filteredPolicies.length})
                                </span>
                            </h3>
                            {/* Status Tabs */}
                            <div className="flex bg-gray-100 dark:bg-slate-700 rounded-xl p-1 gap-1">
                                {[
                                    { key: 'all', label: 'All' },
                                    { key: 'paid', label: 'Active' },
                                    { key: 'unpaid', label: 'Pending' },
                                ].map((status) => (
                                    <button
                                        key={status.key}
                                        onClick={() => setStatusFilter(status.key)}
                                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${statusFilter === status.key
                                                ? 'bg-white dark:bg-slate-600 text-[#3a663f] dark:text-green-400 shadow-sm'
                                                : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300'
                                            }`}
                                    >
                                        {status.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {filteredPolicies.length > 0 ? (
                            <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
                                {filteredPolicies.slice(0, 6).map((policy) => (
                                    <PolicyCard key={policy.id} policy={policy} />
                                ))}
                            </div>
                        ) : (
                            <EmptyState
                                message="No policies found for this status."
                                statusFilter={statusFilter}
                            />
                        )}

                        {filteredPolicies.length > 6 && (
                            <div className="mt-4 text-center">
                                <Link
                                    to="/policies"
                                    className="inline-flex items-center gap-2 text-sm text-[#3a663f] dark:text-green-400 hover:text-[#2d5231] font-medium transition-colors"
                                >
                                    View all {filteredPolicies.length} policies
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ========== WORLD-CLASS STAT CARD ==========
function StatCard({ icon: Icon, label, value, gradient }) {
    return (
        <div
            className={`relative group overflow-hidden rounded-xl bg-gradient-to-br ${gradient} text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}
        >
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-white/5 blur-xl" />

            <div className="relative p-5 flex flex-col justify-between h-full">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-3 backdrop-blur-sm group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6" />
                </div>

                <div className="space-y-0.5">
                    <p className="text-xs font-semibold uppercase tracking-wider opacity-80">
                        {label}
                    </p>
                    <p className="text-2xl font-extrabold tracking-tight">{value}</p>
                </div>
            </div>
        </div>
    );
}

// ========== POLICY CARD ==========
function PolicyCard({ policy }) {
    const isActive = policy.payment_status === 'paid' || policy.payment_status === 'active';

    return (
        <div className="group relative bg-white dark:bg-slate-800 rounded-xl shadow-md border border-gray-200 dark:border-slate-700 p-5 hover:shadow-xl hover:border-[#3a663f]/30 dark:hover:border-green-400/30 transition-all duration-300 hover:-translate-y-1">
            <div className="flex justify-between items-start mb-3">
                <div className="min-w-0">
                    <p className="text-xs text-gray-400 dark:text-slate-500 uppercase tracking-wider">Policy No.</p>
                    <p className="font-bold text-gray-800 dark:text-white mt-0.5 truncate">
                        {policy.policy_number}
                    </p>
                </div>
                <Badge status={policy.payment_status} />
            </div>

            <div className="space-y-2.5 text-sm">
                <div className="flex items-center gap-2 text-gray-600 dark:text-slate-300">
                    <Ship className="w-4 h-4 text-[#3a663f] dark:text-green-400 flex-shrink-0" />
                    <span className="truncate">{policy.vessel_name || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 dark:text-slate-300">
                    <DollarSign className="w-4 h-4 text-[#3a663f] dark:text-green-400 flex-shrink-0" />
                    <span>Sum Insured: <strong>${Number(policy.sum_insured).toLocaleString()}</strong></span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 dark:text-slate-300">
                    <Calendar className="w-4 h-4 text-[#3a663f] dark:text-green-400 flex-shrink-0" />
                    <span>Issued: {new Date(policy.issued_date).toLocaleDateString()}</span>
                </div>
            </div>

            <Link
                to={`/policies/${policy.id}`}
                className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#3a663f] dark:text-green-400 hover:text-[#2d5231] dark:hover:text-green-300 transition-colors"
            >
                View Details
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>

            <div className="absolute inset-0 bg-[#3a663f]/5 dark:bg-green-400/5 rounded-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity" />
        </div>
    );
}

// ========== EMPTY STATE ==========
function EmptyState({ message, statusFilter }) {
    return (
        <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 dark:bg-slate-700 mb-6">
                <FileText className="w-10 h-10 text-gray-300 dark:text-slate-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 dark:text-slate-300 mb-2">No policies found</h3>
            <p className="text-gray-400 dark:text-slate-500 max-w-sm mx-auto">{message}</p>
            {statusFilter === 'all' && (
                <Link
                    to="/cargo"
                    className="mt-6 inline-flex items-center gap-2 bg-[#3a663f] hover:bg-[#2d5231] text-white font-semibold py-3 px-6 rounded-xl transition-colors shadow-lg hover:shadow-xl"
                >
                    <Plus className="w-5 h-5" />
                    Get a Cargo Certificate
                </Link>
            )}
        </div>
    );
}

// ========== SKELETON LOADER ==========
function SkeletonLoader() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="animate-pulse space-y-6">
                    <div className="h-48 bg-gray-200 dark:bg-slate-700 rounded-2xl" />
                    <div className="grid grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-28 bg-gray-100 dark:bg-slate-700 rounded-xl" />
                        ))}
                    </div>
                    <div className="grid grid-cols-3 gap-6">
                        <div className="h-80 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                        <div className="col-span-2 grid grid-cols-2 gap-4">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="h-48 bg-gray-100 dark:bg-slate-700 rounded-xl" />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}